/**
 * useDragScroll — drag-to-scroll horizontal fluide à la souris (desktop), en
 * plus du scroll natif tactile/trackpad. À brancher sur un conteneur
 * `overflow-x-auto`.
 *
 * Le contenu draggable ne doit PAS être cliquable (utiliser des <div>), seuls
 * des sous-éléments ciblés (ex: un lien « Découvrir ») restent interactifs.
 * Le composable neutralise quand même un clic si un vrai drag a eu lieu.
 *
 * Usage :
 * ```ts
 * const scroller = ref<HTMLElement | null>(null);
 * useDragScroll(scroller);
 * ```
 */
export function useDragScroll(el: Ref<HTMLElement | null>) {
  let isDown = false;
  let startX = 0;
  let startScroll = 0;
  let moved = false;

  // Vrai dès que le conteneur a été scrollé horizontalement (> 4px).
  // Sert à n'afficher le fondu gauche qu'une fois le défilement commencé.
  const hasScrolled = ref(false);

  const updateScrolled = () => {
    const node = el.value;
    if (node) hasScrolled.value = node.scrollLeft > 4;
  };

  const onPointerDown = (e: PointerEvent) => {
    if (e.pointerType !== "mouse") return;
    const node = el.value;
    if (!node) return;
    isDown = true;
    moved = false;
    startX = e.clientX;
    startScroll = node.scrollLeft;
    node.style.cursor = "grabbing";
    // Désactive le snap pendant le drag pour un défilement continu et fluide.
    node.style.scrollSnapType = "none";
    node.style.scrollBehavior = "auto";
  };

  const onPointerMove = (e: PointerEvent) => {
    if (!isDown) return;
    const node = el.value;
    if (!node) return;
    const dx = e.clientX - startX;
    if (Math.abs(dx) > 4) moved = true;
    node.scrollLeft = startScroll - dx;
    updateScrolled();
  };

  const endDrag = () => {
    if (!isDown) return;
    isDown = false;
    const node = el.value;
    if (!node) return;
    node.style.cursor = "grab";
    // Réactive le snap une fois le drag terminé.
    node.style.scrollSnapType = "";
    node.style.scrollBehavior = "";
  };

  // Si un drag a eu lieu, neutralise le clic (évite d'ouvrir un lien enfant).
  const onClickCapture = (e: MouseEvent) => {
    if (moved) {
      e.preventDefault();
      e.stopPropagation();
      moved = false;
    }
  };

  const onDragStart = (e: DragEvent) => e.preventDefault();

  onMounted(() => {
    const node = el.value;
    if (!node) return;
    node.style.cursor = "grab";
    node.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", endDrag);
    node.addEventListener("click", onClickCapture, true);
    node.addEventListener("dragstart", onDragStart);
    node.addEventListener("scroll", updateScrolled, { passive: true });
  });

  onBeforeUnmount(() => {
    const node = el.value;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", endDrag);
    if (node) {
      node.removeEventListener("pointerdown", onPointerDown);
      node.removeEventListener("click", onClickCapture, true);
      node.removeEventListener("dragstart", onDragStart);
      node.removeEventListener("scroll", updateScrolled);
    }
  });

  return { hasScrolled };
}
